import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';

function Point5({ projectId }) {
  const [activeTab, setActiveTab] = useState(0);
  const [accessStrategies, setAccessStrategies] = useState(null);
  const [operationalPlan, setOperationalPlan] = useState(null);
  const [financialPlan, setFinancialPlan] = useState(null);
  const [implementationTimeline, setImplementationTimeline] = useState(null);
  const [masterAccessPlan, setMasterAccessPlan] = useState(null);
  const [loading, setLoading] = useState(false);

  const [targetMarkets, setTargetMarkets] = useState('Francia, Alemania, Portugal');
  const [entryModes, setEntryModes] = useState(['Exportación Directa', 'Agente/Distribuidor', 'E-commerce/Digital']);
  const [businessModel, setBusinessModel] = useState('B2B');
  const [planComponents, setPlanComponents] = useState(['Estructura Legal', 'Recursos Humanos', 'Marketing y Comunicación', 'Logística y Distribución']);
  const [implementationHorizon, setImplementationHorizon] = useState('18 meses');
  const [companySize, setCompanySize] = useState('Mediana');
  const [initialInvestment, setInitialInvestment] = useState(100000);
  const [revenueTarget, setRevenueTarget] = useState(500000);
  const [marketsBudget, setMarketsBudget] = useState('Francia: 40%, Alemania: 35%, Portugal: 25%');
  const [financialComponents, setFinancialComponents] = useState(['Costes de Establecimiento', 'Marketing y Comunicación', 'Recursos Humanos', 'Capital de Trabajo']);
  const [timelineDuration, setTimelineDuration] = useState('18 meses');
  const [phasesApproach, setPhasesApproach] = useState('Secuencial (mercado por mercado)');
  const [priorityActivities, setPriorityActivities] = useState(['Constitución Legal', 'Lanzamiento Marketing', 'Cierre Primeros Clientes']);
  const [includeMilestones, setIncludeMilestones] = useState(true);
  const [includeDependencies, setIncludeDependencies] = useState(true);
  const [includeTimelineResources, setIncludeTimelineResources] = useState(true);

  const entryModeOptions = [
    'Exportación Directa', 'Exportación Indirecta', 'Agente/Distribuidor',
    'Filial Comercial', 'Joint Venture', 'Franchising', 'Licencing',
    'Inversión Directa', 'E-commerce/Digital'
  ];

  const planComponentOptions = [
    'Estructura Legal', 'Recursos Humanos', 'Marketing y Comunicación',
    'Logística y Distribución', 'Tecnología y Sistemas', 'Finanzas y Control',
    'Cumplimiento Legal', 'Gestión de Riesgos'
  ];

  const financialComponentOptions = [
    'Costes de Establecimiento', 'Marketing y Comunicación', 'Recursos Humanos',
    'Tecnología y Sistemas', 'Logística', 'Legal y Administrativo',
    'Capital de Trabajo', 'Contingencias'
  ];

  const priorityActivityOptions = [
    'Constitución Legal', 'Contratación RRHH', 'Setup Tecnológico',
    'Lanzamiento Marketing', 'Establecimiento Logístico', 'Cierre Primeros Clientes'
  ];

  useEffect(() => {
    loadStoredData();
  }, [projectId]);

  const loadStoredData = () => {
    setAccessStrategies(getFromStorage(`access_strategies_${projectId}`));
    setOperationalPlan(getFromStorage(`operational_plan_${projectId}`));
    setFinancialPlan(getFromStorage(`financial_plan_${projectId}`));
    setImplementationTimeline(getFromStorage(`implementation_timeline_${projectId}`));
    setMasterAccessPlan(getFromStorage(`master_access_plan_${projectId}`));
  };

  const getFromStorage = (key) => {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  };

  const saveToStorage = (key, data) => {
    localStorage.setItem(key, JSON.stringify(data));
  };

  const analyzeWithLLM = async (pointNumber, prompt, useWeb = false, searchTerms = '') => {
    setLoading(true);
    try {
      const response = await axios.post('/api/analysis/', {
        project_id: projectId,
        point_number: pointNumber,
        prompt,
        use_web: useWeb,
        search_terms: searchTerms,
        web_only: false
      });
      return response.data;
    } catch (error) {
      console.error('Error en análisis:', error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const analyzeStrategies = async () => {
    if (!targetMarkets || !entryModes.length) return;

    const contextInfo = localStorage.getItem(`final_recommendations_${projectId}`) 
      ? `\n\nCONTEXTO DEL ANÁLISIS PREVIO:\n${JSON.parse(localStorage.getItem(`final_recommendations_${projectId}`)).content}`
      : '';

    const prompt = `Analiza las estrategias de entrada a mercados internacionales:

MERCADOS OBJETIVO: ${targetMarkets}
MODALIDADES A EVALUAR: ${entryModes.join(', ')}
MODELO DE NEGOCIO: ${businessModel}

Para cada mercado y modalidad, analiza:

1. MODALIDADES DE ENTRADA:
   - Ventajas y desventajas de cada modalidad
   - Inversión requerida
   - Nivel de control
   - Riesgo asociado
   - Tiempo de implementación

2. RECOMENDACIÓN POR MERCADO:
   - Modalidad óptima para cada mercado
   - Justificación de la elección
   - Factores clave de decisión

3. ESTRATEGIA COMERCIAL:
   - Canales de distribución recomendados
   - Estructura de precios
   - Política de comunicación
   - Adaptaciones necesarias del producto/servicio

4. SOCIOS ESTRATÉGICOS:
   - Tipo de socios a buscar
   - Perfil ideal del partner
   - Modelo de colaboración

Proporciona análisis detallado y recomendaciones específicas.${contextInfo}`;

    const result = await analyzeWithLLM(
      51, 
      prompt, 
      true, 
      `estrategias entrada mercados ${businessModel} modalidades exportación`
    );
    
    if (result) {
      setAccessStrategies(result);
      saveToStorage(`access_strategies_${projectId}`, result);
    }
  };

  const generateOperationalPlan = async () => {
    if (!planComponents.length) return;

    const strategiesContent = accessStrategies?.content || 'Generar plan operativo general';

    const prompt = `Desarrolla un plan operativo detallado para la internacionalización:

ESTRATEGIAS BASE:
${strategiesContent}

PARÁMETROS:
- Componentes a incluir: ${planComponents.join(', ')}
- Horizonte temporal: ${implementationHorizon}
- Tamaño empresa: ${companySize}

ESTRUCTURA DEL PLAN OPERATIVO:

1. RESUMEN EJECUTIVO
   - Objetivos de internacionalización
   - Mercados prioritarios
   - Modalidades de entrada elegidas

2. PARA CADA COMPONENTE SELECCIONADO:
   
   ESTRUCTURA LEGAL:
   - Forma jurídica recomendada por mercado
   - Trámites y requisitos
   - Costes de constitución
   - Plazos estimados
   
   RECURSOS HUMANOS:
   - Perfiles necesarios
   - Estructura organizativa
   - Plan de contratación
   - Costes salariales estimados
   
   MARKETING Y COMUNICACIÓN:
   - Estrategia de posicionamiento
   - Canales de comunicación
   - Presupuesto marketing
   - Calendario de campañas
   
   LOGÍSTICA Y DISTRIBUCIÓN:
   - Modelo logístico recomendado
   - Socios logísticos
   - Centros de distribución
   - Costes logísticos estimados
   
   TECNOLOGÍA Y SISTEMAS:
   - Infraestructura TI necesaria
   - Sistemas de gestión
   - Plataformas e-commerce
   - Inversión tecnológica
   
   FINANZAS Y CONTROL:
   - Estructura financiera
   - Sistema de control de gestión
   - Indicadores KPI
   - Reporting internacional
   
   CUMPLIMIENTO LEGAL:
   - Normativas por mercado
   - Certificaciones necesarias
   - Protección de marca
   - Contratos tipo
   
   GESTIÓN DE RIESGOS:
   - Identificación de riesgos
   - Planes de contingencia
   - Seguros recomendados
   - Monitorización de riesgos

3. CRONOGRAMA INTEGRADO
4. RESPONSABILIDADES Y ROLES
5. MÉTRICAS DE SEGUIMIENTO

Proporciona un plan accionable y específico.`;

    const result = await analyzeWithLLM(
      52, 
      prompt, 
      true, 
      'plan operativo internacionalización empresas pasos implementación'
    );
    
    if (result) {
      setOperationalPlan(result);
      saveToStorage(`operational_plan_${projectId}`, result);
    }
  };

  const generateBudget = async () => {
    const operationalContent = operationalPlan?.content || 'Generar presupuesto general de internacionalización';

    const prompt = `Desarrolla un presupuesto detallado para la internacionalización:

PLAN OPERATIVO BASE:
${operationalContent}

PARÁMETROS FINANCIEROS:
- Inversión inicial disponible: ${initialInvestment.toLocaleString()} €
- Objetivo facturación año 1: ${revenueTarget.toLocaleString()} €
- Distribución por mercados: ${marketsBudget}
- Componentes a presupuestar: ${financialComponents.join(', ')}

ESTRUCTURA DEL PRESUPUESTO:

1. INVERSIÓN INICIAL REQUERIDA:
   Por cada componente seleccionado:
   - Costes estimados detallados
   - Timing de la inversión
   - Justificación de los importes

2. CUENTA DE RESULTADOS PROYECTADA (3 años):
   - Ingresos por mercado y año
   - Costes operativos recurrentes
   - Margen bruto y neto
   - Punto de equilibrio

3. FLUJO DE CAJA:
   - Flujo mensual primer año
   - Flujo trimestral años 2-3
   - Necesidades de financiación

4. ANÁLISIS DE RENTABILIDAD:
   - ROI esperado
   - Payback period
   - NPV y TIR si procede

5. ANÁLISIS DE SENSIBILIDAD:
   - Escenarios optimista/pesimista
   - Variables críticas
   - Puntos de riesgo financiero

6. FINANCIACIÓN RECOMENDADA:
   - Fuentes de financiación
   - Ayudas y subvenciones disponibles
   - Estructura de capital recomendada

Proporciona cifras realistas y justificadas.`;

    const result = await analyzeWithLLM(
      53, 
      prompt, 
      true, 
      'presupuesto internacionalización empresas costes inversión ROI'
    );
    
    if (result) {
      setFinancialPlan(result);
      saveToStorage(`financial_plan_${projectId}`, result);
    }
  };

  const generateTimeline = async () => {
    let timelineContext = '';
    if (operationalPlan) timelineContext += `PLAN OPERATIVO:\n${operationalPlan.content}\n\n`;
    if (accessStrategies) timelineContext += `ESTRATEGIAS:\n${accessStrategies.content}\n\n`;

    const prompt = `Desarrolla un cronograma detallado de implementación:

${timelineContext}

PARÁMETROS:
- Duración total: ${timelineDuration}
- Enfoque: ${phasesApproach}
- Actividades prioritarias: ${priorityActivities.join(', ')}

ESTRUCTURA DEL CRONOGRAMA:

1. FASES PRINCIPALES:
   - Fase 1: Preparación y Setup
   - Fase 2: Lanzamiento
   - Fase 3: Consolidación
   - Fase 4: Optimización

2. CRONOGRAMA DETALLADO:
   Por cada mes/trimestre incluir:
   - Actividades específicas a realizar
   - Responsables de cada actividad
   ${includeMilestones ? '- Hitos clave y entregables' : ''}
   ${includeDependencies ? '- Dependencias entre actividades' : ''}
   ${includeTimelineResources ? '- Recursos necesarios por período' : ''}

3. RUTA CRÍTICA:
   - Actividades críticas que no pueden retrasarse
   - Buffers y contingencias
   - Puntos de control y revisión

4. PLAN DE RIESGOS:
   - Riesgos de planificación identificados
   - Planes de contingencia
   - Indicadores de alerta temprana

Proporciona un cronograma realista y ejecutable.`;

    const result = await analyzeWithLLM(55, prompt, false);
    
    if (result) {
      setImplementationTimeline(result);
      saveToStorage(`implementation_timeline_${projectId}`, result);
    }
  };

  const generateSummary = async (content, maxChars = 6000) => {
    // Si el contenido ya es menor al límite, devolverlo completo
    if (content.length <= maxChars) {
      return content;
    }

    const summaryPrompt = `Resume manteniendo los puntos más importantes de este análisis:

${content}

INSTRUCCIONES:
- Mantén información clave y conclusiones
- Preserva datos numéricos relevantes
- Estructura clara
- Máximo ${Math.floor(maxChars/6)} palabras aproximadamente`;

    const result = await analyzeWithLLM(99, summaryPrompt, false);
    return result?.content || content.substring(0, maxChars);
  };

  const generateMasterPlan = async () => {
    console.log('generateMasterPlan iniciada');
    
    setLoading(true);
    
    try {
      // Recopilar contenido disponible con límites estrictos
      let allContent = '';
      const maxSectionSize = 4000; // Límite muy conservador por sección
      
      if (accessStrategies) {
        const content = accessStrategies.content.substring(0, maxSectionSize);
        allContent += `# ESTRATEGIAS DE ACCESO\n\n${content}\n\n`;
      }
      
      if (operationalPlan) {
        const content = operationalPlan.content.substring(0, maxSectionSize);
        allContent += `# PLAN OPERATIVO\n\n${content}\n\n`;
      }
      
      if (financialPlan) {
        const content = financialPlan.content.substring(0, maxSectionSize);
        allContent += `# ANÁLISIS FINANCIERO\n\n${content}\n\n`;
      }
      
      if (implementationTimeline) {
        const content = implementationTimeline.content.substring(0, maxSectionSize);
        allContent += `# CRONOGRAMA\n\n${content}\n\n`;
      }

      // Verificar tamaño total y truncar si es necesario
      if (allContent.length > 20000) {
        console.log('Contenido muy grande, aplicando truncamiento adicional');
        allContent = allContent.substring(0, 20000) + '\n\n[Contenido truncado para optimización...]';
      }

      console.log('Tamaño del prompt final:', allContent.length);

      const prompt = `Crea un Plan de Acceso a Mercados Internacionales integrado basándote en:

${allContent}

ESTRUCTURA DEL PLAN MAESTRO:

1. RESUMEN EJECUTIVO
   - Mercados objetivo y estrategia
   - Inversión y ROI esperado
   - Timeline principal

2. ESTRATEGIA CONSOLIDADA
   - Modalidades de entrada recomendadas
   - Justificación de decisiones clave

3. PLAN DE IMPLEMENTACIÓN
   - Fases principales del proyecto
   - Cronograma integrado
   - Recursos necesarios

4. ANÁLISIS FINANCIERO
   - Presupuesto consolidado
   - Proyecciones y rentabilidad

5. RIESGOS Y CONTINGENCIAS
   - Principales riesgos identificados
   - Planes de mitigación

6. PRÓXIMOS PASOS
   - Acciones inmediatas (30 días)
   - Hitos clave (3-12 meses)

Genera un documento ejecutivo profesional e integrado.`;

      const result = await analyzeWithLLM(56, prompt, false);
      
      if (result) {
        setMasterAccessPlan(result);
        saveToStorage(`master_access_plan_${projectId}`, result);
        console.log('Plan maestro generado exitosamente');
      }
    } catch (error) {
      console.error('Error generando plan maestro:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadContent = (content, filename) => {
    const element = document.createElement('a');
    const file = new Blob([content], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = filename;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleEntryModeChange = (mode) => {
    if (entryModes.includes(mode)) {
      setEntryModes(entryModes.filter(m => m !== mode));
    } else {
      setEntryModes([...entryModes, mode]);
    }
  };

  const handlePlanComponentChange = (component) => {
    if (planComponents.includes(component)) {
      setPlanComponents(planComponents.filter(c => c !== component));
    } else {
      setPlanComponents([...planComponents, component]);
    }
  };

  const handleFinancialComponentChange = (component) => {
    if (financialComponents.includes(component)) {
      setFinancialComponents(financialComponents.filter(c => c !== component));
    } else {
      setFinancialComponents([...financialComponents, component]);
    }
  };

  const handlePriorityActivityChange = (activity) => {
    if (priorityActivities.includes(activity)) {
      setPriorityActivities(priorityActivities.filter(a => a !== activity));
    } else {
      setPriorityActivities([...priorityActivities, activity]);
    }
  };

  const getCompletionStatus = () => {
    const components = {
      'Estrategias de Acceso': !!accessStrategies,
      'Plan Operativo': !!operationalPlan,
      'Análisis Financiero': !!financialPlan,
      'Cronograma': !!implementationTimeline
    };
    
    const completed = Object.values(components).filter(Boolean).length;
    const total = Object.keys(components).length;
    
    return { components, completed, total, percentage: (completed / total) * 100 };
  };

  const tabs = [
    'Estrategias',
    'Plan Operativo',
    'Presupuesto',
    'Cronograma',
    'Dashboard'
  ];

  return (
    <div className="point-content">
      <h2>5. Plan de Acceso a Mercados</h2>
      
      <div className="tabs">
        <div className="tab-buttons">
          {tabs.map((tab, index) => (
            <button
              key={index}
              className={`tab-btn ${activeTab === index ? 'active' : ''}`}
              onClick={() => setActiveTab(index)}
            >
              {tab}
            </button>
          ))}
        </div>

        {activeTab === 0 && (
          <div className="tab-content">
            <h3>Modalidades de Entrada y Estrategias</h3>
            
            <div className="two-cols">
              <div className="col-left">
                <h4>Mercados objetivo:</h4>
                <textarea
                  value={targetMarkets}
                  onChange={(e) => setTargetMarkets(e.target.value)}
                  rows={3}
                />

                <h4>Modalidades de entrada:</h4>
                <div className="checkbox-grid">
                  {entryModeOptions.map(mode => (
                    <label key={mode}>
                      <input
                        type="checkbox"
                        checked={entryModes.includes(mode)}
                        onChange={() => handleEntryModeChange(mode)}
                      />
                      {mode}
                    </label>
                  ))}
                </div>

                <div className="template-selector">
                  <label>Modelo de negocio:</label>
                  <select 
                    value={businessModel}
                    onChange={(e) => setBusinessModel(e.target.value)}
                  >
                    <option value="B2B">B2B</option>
                    <option value="B2C">B2C</option>
                    <option value="B2B2C">B2B2C</option>
                    <option value="Marketplace">Marketplace</option>
                    <option value="Mixto">Mixto</option>
                  </select>
                </div>

                <button 
                  onClick={analyzeStrategies}
                  disabled={loading || !targetMarkets || !entryModes.length}
                  className="analyze-btn"
                >
                  {loading ? 'Analizando estrategias...' : 'Analizar Estrategias'}
                </button>
              </div>

              <div className="col-right">
                {accessStrategies ? (
                  <div className="analysis-result">
                    <h4>Estrategias de Acceso</h4>
                    <div className="result-content"><ReactMarkdown>{accessStrategies.content}</ReactMarkdown></div>
                    {accessStrategies.sources && (
                      <div className="sources">
                        <h5>Fuentes utilizadas:</h5>
                        <ul>
                          {accessStrategies.sources.map((source, index) => (
                            <li key={index}>{source}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="info-box">
                    Configura mercados y modalidades, luego ejecuta el análisis
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 1 && (
          <div className="tab-content">
            <h3>Plan Operativo Detallado</h3>
            
            <div className="two-cols">
              <div className="col-left">
                {accessStrategies ? (
                  <div className="info-box success">Estrategias definidas</div>
                ) : (
                  <div className="info-box warning">Define primero las estrategias</div>
                )}

                <h4>Componentes del plan:</h4>
                <div className="checkbox-grid">
                  {planComponentOptions.map(component => (
                    <label key={component}>
                      <input
                        type="checkbox"
                        checked={planComponents.includes(component)}
                        onChange={() => handlePlanComponentChange(component)}
                      />
                      {component}
                    </label>
                  ))}
                </div>

                <div className="template-selector">
                  <label>Horizonte de implementación:</label>
                  <select 
                    value={implementationHorizon}
                    onChange={(e) => setImplementationHorizon(e.target.value)}
                  >
                    <option value="6 meses">6 meses</option>
                    <option value="1 año">1 año</option>
                    <option value="18 meses">18 meses</option>
                    <option value="2 años">2 años</option>
                    <option value="3 años">3 años</option>
                  </select>
                </div>

                <div className="template-selector">
                  <label>Tamaño de empresa:</label>
                  <select 
                    value={companySize}
                    onChange={(e) => setCompanySize(e.target.value)}
                  >
                    <option value="Startup/Pequeña">Startup/Pequeña</option>
                    <option value="Mediana">Mediana</option>
                    <option value="Grande">Grande</option>
                    <option value="Multinacional">Multinacional</option>
                  </select>
                </div>

                <button 
                  onClick={generateOperationalPlan}
                  disabled={loading || !planComponents.length}
                  className="analyze-btn"
                >
                  {loading ? 'Generando plan...' : 'Generar Plan Operativo'}
                </button>
              </div>

              <div className="col-right">
                {operationalPlan ? (
                  <div className="analysis-result">
                    <h4>Plan Operativo</h4>
                    <div className="result-content"><ReactMarkdown>{operationalPlan.content}</ReactMarkdown></div>
                    <button 
                      onClick={() => downloadContent(operationalPlan.content, 'Plan_Operativo.txt')}
                      className="analyze-btn secondary"
                    >
                      Exportar Plan Operativo
                    </button>
                  </div>
                ) : (
                  <div className="info-box">
                    Selecciona componentes y genera el plan operativo
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 2 && (
          <div className="tab-content">
            <h3>Presupuesto y Análisis Financiero</h3>
            
            <div className="two-cols">
              <div className="col-left">
                <h4>Parámetros financieros:</h4>
                
                <div className="input-group">
                  <label>Inversión inicial disponible (€):</label>
                  <input
                    type="number"
                    value={initialInvestment}
                    onChange={(e) => setInitialInvestment(Number(e.target.value))}
                    step={10000}
                  />
                </div>

                <div className="input-group">
                  <label>Objetivo facturación año 1 (€):</label>
                  <input
                    type="number"
                    value={revenueTarget}
                    onChange={(e) => setRevenueTarget(Number(e.target.value))}
                    step={50000}
                  />
                </div>

                <div className="input-group">
                  <label>Distribución presupuesto por mercado:</label>
                  <textarea
                    value={marketsBudget}
                    onChange={(e) => setMarketsBudget(e.target.value)}
                    rows={3}
                  />
                </div>

                <h4>Elementos del presupuesto:</h4>
                <div className="checkbox-grid">
                  {financialComponentOptions.map(component => (
                    <label key={component}>
                      <input
                        type="checkbox"
                        checked={financialComponents.includes(component)}
                        onChange={() => handleFinancialComponentChange(component)}
                      />
                      {component}
                    </label>
                  ))}
                </div>

                <button 
                  onClick={generateBudget}
                  disabled={loading}
                  className="analyze-btn"
                >
                  {loading ? 'Generando presupuesto...' : 'Generar Presupuesto'}
                </button>
              </div>

              <div className="col-right">
                {financialPlan ? (
                  <div className="analysis-result">
                    <h4>Análisis Financiero</h4>
                    <div className="result-content"><ReactMarkdown>{financialPlan.content}</ReactMarkdown></div>
                    <button 
                      onClick={() => downloadContent(financialPlan.content, 'Analisis_Financiero.txt')}
                      className="analyze-btn secondary"
                    >
                      Exportar Análisis
                    </button>
                  </div>
                ) : (
                  <div className="info-box">
                    Configura parámetros y genera el presupuesto
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 3 && (
          <div className="tab-content">
            <h3>Cronograma de Implementación</h3>
            
            <div className="two-cols">
              <div className="col-left">
                <h4>Configuración del cronograma:</h4>

                <div className="template-selector">
                  <label>Duración total del proyecto:</label>
                  <select 
                    value={timelineDuration}
                    onChange={(e) => setTimelineDuration(e.target.value)}
                  >
                    <option value="6 meses">6 meses</option>
                    <option value="12 meses">12 meses</option>
                    <option value="18 meses">18 meses</option>
                    <option value="24 meses">24 meses</option>
                    <option value="36 meses">36 meses</option>
                  </select>
                </div>

                <div className="template-selector">
                  <label>Enfoque de implementación:</label>
                  <select 
                    value={phasesApproach}
                    onChange={(e) => setPhasesApproach(e.target.value)}
                  >
                    <option value="Secuencial (mercado por mercado)">Secuencial (mercado por mercado)</option>
                    <option value="Paralelo (todos simultáneamente)">Paralelo (todos simultáneamente)</option>
                    <option value="Híbrido (grupos de mercados)">Híbrido (grupos de mercados)</option>
                  </select>
                </div>

                <h4>Actividades prioritarias:</h4>
                <div className="checkbox-grid">
                  {priorityActivityOptions.map(activity => (
                    <label key={activity}>
                      <input
                        type="checkbox"
                        checked={priorityActivities.includes(activity)}
                        onChange={() => handlePriorityActivityChange(activity)}
                      />
                      {activity}
                    </label>
                  ))}
                </div>

                <label>
                  <input
                    type="checkbox"
                    checked={includeMilestones}
                    onChange={(e) => setIncludeMilestones(e.target.checked)}
                  />
                  Incluir hitos clave
                </label>

                <label>
                  <input
                    type="checkbox"
                    checked={includeDependencies}
                    onChange={(e) => setIncludeDependencies(e.target.checked)}
                  />
                  Incluir dependencias
                </label>

                <label>
                  <input
                    type="checkbox"
                    checked={includeTimelineResources}
                    onChange={(e) => setIncludeTimelineResources(e.target.checked)}
                  />
                  Incluir asignación recursos
                </label>

                <button 
                  onClick={generateTimeline}
                  disabled={loading}
                  className="analyze-btn"
                >
                  {loading ? 'Generando cronograma...' : 'Generar Cronograma'}
                </button>
              </div>

              <div className="col-right">
                {implementationTimeline ? (
                  <div className="analysis-result">
                    <h4>Cronograma de Implementación</h4>
                    <div className="result-content"><ReactMarkdown>{implementationTimeline.content}</ReactMarkdown></div>
                    <button 
                      onClick={() => downloadContent(implementationTimeline.content, 'Cronograma.txt')}
                      className="analyze-btn secondary"
                    >
                      Exportar Cronograma
                    </button>
                  </div>
                ) : (
                  <div className="info-box">
                    Configura parámetros y genera el cronograma
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 4 && (
          <div className="tab-content">
            <h3>Dashboard Ejecutivo - Plan de Acceso</h3>
            
            {(() => {
              const status = getCompletionStatus();
              return (
                <div>
                  <div className="progress-section">
                    <h4>Progreso del Plan de Acceso: {status.percentage.toFixed(0)}%</h4>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${status.percentage}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="status-grid">
                    {Object.entries(status.components).map(([name, completed]) => (
                      <div key={name} className={`status-card ${completed ? 'completed' : 'pending'}`}>
                        <span className="status-icon">{completed ? '✅' : '⭕'}</span>
                        <span className="status-name">{name}</span>
                      </div>
                    ))}
                  </div>

                  {status.completed >= 2 ? (
                    <div className="two-cols">
                      <div className="col-left">
                        <h4>Generar documentos finales:</h4>
                        
                        <button 
                          onClick={() => {
                            console.log('Botón clickeado!');
                            generateMasterPlan();
                          }}
                          disabled={loading}
                          className="analyze-btn"
                        >
                          {loading ? 'Generando plan completo...' : 'Plan de Acceso Completo'}
                        </button>

                        {masterAccessPlan && (
                          <button 
                            onClick={() => {
                              const prompt = `Crea un resumen ejecutivo para la dirección de la empresa:

PLAN DE ACCESO DESARROLLADO:
${masterAccessPlan.content}

RESUMEN EJECUTIVO (máximo 800 palabras):

1. OPORTUNIDAD DE NEGOCIO
   - Mercados objetivo y potencial
   - Ventajas competitivas para internacionalización

2. ESTRATEGIA RECOMENDADA
   - Modalidades de entrada elegidas
   - Justificación estratégica

3. INVERSIÓN Y RETORNO
   - Inversión total requerida
   - ROI esperado y timeline de retorno
   - Principales drivers de rentabilidad

4. IMPLEMENTACIÓN
   - Fases clave y duración
   - Recursos necesarios
   - Riesgos principales y mitigación

5. DECISIÓN REQUERIDA
   - Recomendación final
   - Próximos pasos si se aprueba
   - Timeline para decisión

Formato profesional para presentación a comité de dirección.`;

                              analyzeWithLLM(57, prompt, false).then(result => {
                                if (result) {
                                  downloadContent(result.content, 'Resumen_Ejecutivo_Plan_Acceso.txt');
                                }
                              });
                            }}
                            disabled={loading}
                            className="analyze-btn secondary"
                          >
                            Resumen para Dirección
                          </button>
                        )}
                      </div>

                      <div className="col-right">
                        {masterAccessPlan ? (
                          <div className="analysis-result">
                            <h4>Plan de Acceso Completo</h4>
                            <div className="result-content"><ReactMarkdown>{masterAccessPlan.content}</ReactMarkdown></div>
                            <button 
                              onClick={() => downloadContent(masterAccessPlan.content, 'Plan_Acceso_Completo.txt')}
                              className="analyze-btn secondary"
                            >
                              Descargar Plan Completo
                            </button>
                          </div>
                        ) : (
                          <div className="info-box">
                            Genera el Plan de Acceso Completo para ver aquí
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="info-box warning">
                      Completa al menos 2 componentes para generar documentos finales (tienes {status.completed}/4)
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
}

export default Point5;